import { useState } from 'react'
import dayjs from 'dayjs'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, Link, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { MOCK_ASSIGNED_COORDINATOR, MOCK_PIPELINE } from '../data/mockSeed'
import { MiscLogo } from '../components/MiscLogo'
import { PortalShell } from '../components/PortalShell'
import { TopBar } from '../components/TopBar'
import { StatusPill } from '../components/ui'
import { OnboardingThemeProvider } from '../theme/OnboardingThemeProvider'
import { MISC_PRIMARY } from '../theme/onboardingTheme'
import { AppRoutes } from '../../helper/appRoutes'
import { readSavedOnboardingState } from '../context/OnboardingContext'
import { overallPercent } from '../wizard/flow'
import { daysRemaining } from '../lib/session'
import { entityPercent, entityStatus } from '../lib/completion'
import { EntityReview } from '../wizard/steps/StepReview'
import type { PipelineClient } from '../types/onboarding'

const headSx = {
  backgroundColor: '#f8fafc',
  '& .MuiTableCell-head': { fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#64748b', px: 2, py: 1.5 },
} as const
const cellSx = { px: 2, py: 1.5, fontSize: 13 } as const

const fmt = (iso: string | null) => (iso ? dayjs(iso).format('MMM D, YYYY') : '—')

/** Design manual "Status Tracking": client, date started, days remaining, percent complete, date submitted, coordinator. */
export function InternalDashboard() {
  const [expanded, setExpanded] = useState(false)
  const saved = readSavedOnboardingState()

  const liveRow: PipelineClient | null = saved
    ? {
        clientId: 'DEMO',
        clientName: `${saved.clientName} (this browser's demo session)`,
        dateStarted: saved.startedAt,
        percentComplete: overallPercent(saved),
        dateSubmitted: saved.submittedAt,
        assignedCoordinator: MOCK_ASSIGNED_COORDINATOR,
      }
    : null
  const rows = liveRow ? [liveRow, ...MOCK_PIPELINE] : MOCK_PIPELINE

  return (
    <OnboardingThemeProvider>
      <PortalShell
        heroNarrow
        heroTitle="MISC internal — client onboarding pipeline"
        heroSubtitle="Prototype view for staff demo. Rows other than the demo session use fictional clients."
        showHeroLogo
      >
        <TopBar />
        <Box sx={{ display: { xs: 'flex', lg: 'none' }, px: 2, py: 3 }}>
          <MiscLogo />
        </Box>
        <Box sx={{ flex: 1, overflowX: 'auto', px: { xs: 2, lg: 5 }, pb: 6, pt: { xs: 1, lg: 3 } }}>
          <Box sx={{ mx: 'auto', maxWidth: '72rem' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
              <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 600, color: '#0f172a' }}>
                Onboarding pipeline
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Link component={RouterLink} to={AppRoutes.onboarding} sx={{ fontSize: 14, fontWeight: 600, color: MISC_PRIMARY }}>
                  Open onboarding wizard
                </Link>
                <Link component={RouterLink} to={AppRoutes.client.home} sx={{ fontSize: 14, fontWeight: 600, color: MISC_PRIMARY }}>
                  ← Client portal home
                </Link>
              </Box>
            </Box>

            <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 1 }}>
              <Table sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow sx={headSx}>
                    <TableCell>Client Name</TableCell>
                    <TableCell>Date Started</TableCell>
                    <TableCell>Days Remaining</TableCell>
                    <TableCell>Percent Complete</TableCell>
                    <TableCell>Date Submitted</TableCell>
                    <TableCell>Assigned Coordinator</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((c) => (
                    <TableRow key={c.clientId} sx={{ borderTop: '1px solid #f1f5f9' }}>
                      <TableCell sx={{ ...cellSx, fontWeight: 500, color: '#0f172a' }}>{c.clientName}</TableCell>
                      <TableCell sx={cellSx}>{fmt(c.dateStarted)}</TableCell>
                      <TableCell sx={cellSx}>{c.dateSubmitted ? '—' : daysRemaining(c.dateStarted)}</TableCell>
                      <TableCell sx={cellSx}>{c.percentComplete}%</TableCell>
                      <TableCell sx={cellSx}>{fmt(c.dateSubmitted)}</TableCell>
                      <TableCell sx={cellSx}>{c.assignedCoordinator}</TableCell>
                      <TableCell sx={cellSx}>
                        {c.clientId === 'DEMO' ? (
                          <Button size="small" onClick={() => setExpanded((v) => !v)}>
                            {expanded ? 'Hide entities' : 'View entities'}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            {saved && expanded ? (
              <Paper elevation={0} sx={{ mt: 3, borderRadius: '12px', border: '1px solid #e2e8f0', p: 3 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{saved.clientName} — entities</Typography>
                <Typography sx={{ mt: 0.5, mb: 2, fontSize: 13, color: '#475569' }}>
                  Captured data for building the license matrix and login sheet (license matrix is built manually in phase 1).
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                  {saved.entities.map((e) => (
                    <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #e2e8f0', borderRadius: 2, px: 1.5, py: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{e.name || 'Unnamed entity'}</Typography>
                      <StatusPill status={entityStatus(e)} />
                      <Typography sx={{ fontSize: 13 }}>{entityPercent(e)}%</Typography>
                    </Box>
                  ))}
                </Box>
                <Stack spacing={4}>
                  {saved.entities.map((e) => (
                    <EntityReview key={e.id} entity={e} />
                  ))}
                </Stack>
              </Paper>
            ) : null}
          </Box>
        </Box>
      </PortalShell>
    </OnboardingThemeProvider>
  )
}
